import { useEffect, useRef } from 'react';

import { analytics } from '@/analytics/analytics-service';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { consumeAppReloadMarker } from '@/utils/app-reload';
import { resolveRouteId } from '@/utils/resolve-route-id';

/**
 * Reads the Navigation Timing entry for the current document.
 *
 * Its `type` is `'reload'` for a refresh (F5, Cmd-R, toolbar button), `'navigate'` for
 * a fresh load, `'back_forward'` for history traversal and `'prerender'` for a
 * prerendered document. The entry is created once per document load and does not
 * change for SPA route transitions.
 */
const getNavigationEntry = (): PerformanceNavigationTiming | undefined => {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry;
};

/**
 * The pathname of the document as it was loaded.
 *
 * Taken from the timing entry rather than `window.location` because a route
 * `beforeLoad` redirect can move the user before this hook's effect runs, which would
 * otherwise attribute the refresh to the destination page instead of the refreshed one.
 */
const getLoadedPathname = (entry: PerformanceNavigationTiming): string => {
  try {
    return new URL(entry.name).pathname;
  } catch {
    return window.location.pathname;
  }
};

/**
 * Use Track Page Refresh
 * Emits a single `Page Refreshed` event when the current document was loaded by a
 * browser refresh.
 *
 * Amplitude autocapture cannot distinguish a refresh from any other page load — its
 * `Page Viewed` event fires identically for fresh navigations, SPA route changes and
 * history traversal. The Navigation Timing API is the only reliable signal.
 *
 * IMPORTANT: mount this *after* `useAnalyticsIdentify` in the same component. The
 * `org-agent-enrichment` plugin only attaches `Organization` / `Agent` once an
 * identify call has populated its context, and React runs effects in hook order.
 * Tracking is additionally deferred until `orgUrlName` resolves, which is the same
 * precondition that plugin checks.
 *
 * Navigation Timing also reports `'reload'` for `window.location.reload()`, so reloads
 * the app initiates itself (logout paths) must go through `reloadApp()`, whose marker
 * excludes them here.
 *
 * `Current Route` is resolved from the loaded pathname via `resolveRouteId` — the same
 * rule `useCurrentRoute` uses — rather than via that hook, which reads the live location
 * and may already have moved past the URL that was actually refreshed.
 */
export const useTrackPageRefresh = () => {
  const { orgUrlName } = useCurrentFacility();
  const hasTracked = useRef(false);

  useEffect(() => {
    // The navigation entry lives for the whole document, so without this guard the
    // event would re-fire on every route change.
    if (hasTracked.current) return;

    const entry = getNavigationEntry();
    if (entry?.type !== 'reload') return;

    // Checked before waiting on org context so the marker is cleared on the first pass.
    // Consuming clears it, so later passes see false.
    if (consumeAppReloadMarker()) {
      hasTracked.current = true;
      return;
    }

    if (!orgUrlName) return;

    hasTracked.current = true;

    const pathname = getLoadedPathname(entry);
    const currentRoute = resolveRouteId(pathname);

    analytics.track('Page Refreshed', {
      // Omitted rather than sent as undefined when the path is not a facility page,
      // matching how `Beta UI Reverted` represents an absent route.
      ...(currentRoute ? { 'Current Route': currentRoute } : {}),
      'Page Path': pathname,
    });
  }, [orgUrlName]);
};
